import { Discountable } from '@/shared/interfaces/discount';
import { Pagination } from '@/shared/interfaces/pagination';
import { Currency } from './balance';
import useCurrencies from '@/shared/composables/useCurrencies';

const { fiatCurrency } = useCurrencies();

/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
export class TableSpot {
	public id: number;
	public friendly_id: string;
	public manual_location: boolean;
	public manual_location_type: string;
	public manual_location_values: string;
	public table_number: string;
	public public: boolean;

	constructor(data: { id: number; friendly_id: string; manual_location: 0 | 1 | boolean; manual_location_type: string; manual_location_values: string; table_number: string; public?: boolean }) {
		this.id = data.id;
		this.friendly_id = data?.friendly_id || '';
		this.manual_location = !!data?.manual_location || false;
		this.manual_location_type = data?.manual_location_type || '';
		this.manual_location_values = data?.manual_location_values || '';
		this.table_number = data?.table_number || '';
		this.public = !!data?.public || false;
	}

	/**
	 * Array representation of pre defined manual locations
	 */
	get manualLocationValues(): string[] {
		if (this.manual_location_values?.trim() !== '') {
			return this.manual_location_values?.split(',').map((location) => location.trim());
		}

		return [];
	}
}

export class TableSpotLocation {
	public table_number: string;
	constructor(data: { table_number: string }) {
		this.table_number = data.table_number;
	}
}

export class TableSpotCartItem {
	constructor(
		public product: Product,
		public quantity = 0,
		public comments: string[] = [],
	) {}
}

export class TableSpotCart {
	constructor(public items: TableSpotCartItem[] = []) {}

	protected modifyQuantity(product: Product, modifier: number): number | false {
		const tableSpotCartItem = this.items.find((i) => i.product.id === product.id);

		if (!tableSpotCartItem) {
			return false;
		}

		tableSpotCartItem.quantity += modifier;

		return tableSpotCartItem.quantity;
	}

	sumQuantity(product: Product, amount = 1) {
		const newQuantity = this.modifyQuantity(product, amount);

		if (newQuantity === false) {
			// Wasn't modified because it isn't present so we need to add it
			return this.addToCart(product, amount);
		}

		return this;
	}

	subQuantity(product: Product, amount = 1): this {
		const newQuantity = this.modifyQuantity(product, -amount);

		if (newQuantity === false) {
			return this; // isn't present so we just skip
		} else if (newQuantity === 0) {
			return this.removeFromCart(product);
		}

		return this;
	}

	addToCart(product: Product, amount = 1): this {
		// First we check if the item already exists, and if it does we will instead just sum the quantity
		if (this.items.find((i) => i.product.id === product.id)) {
			return this.sumQuantity(product, amount);
		}

		// if it doesn't we add it as a new item
		this.items.push(new TableSpotCartItem(product, amount));

		return this;
	}

	removeFromCart(product: Product): this {
		const indexToRemove = this.items.findIndex((i) => i.product.id === product.id);

		if (indexToRemove !== -1) {
			this.items.splice(indexToRemove, 1);
		}

		return this;
	}

	updateComments(product: Product, comments: string[] = []): this {
		// First we need to find the item
		const tableSpotCartItem = this.items.find((i) => i.product.id === product.id);

		if (!tableSpotCartItem) {
			return this;
		}

		tableSpotCartItem.comments = comments;

		return this;
	}
}

export class ProductCategory {
	public id: number;
	public sale_catalogue_id: number;
	public order: number;
	public name: string;
	public vat_percentage: string;
	public image_url: string;
	public icon: string;
	public icon_color: string;
	public default_currency_id: string | undefined | null;

	constructor(data: { id: number; sale_catalogue_id: number; order: number; name: string; vat_percentage: string; image_url: string; icon: string; icon_color: string; default_currency_id: string | undefined | null }) {
		this.id = data.id;
		this.sale_catalogue_id = data.sale_catalogue_id;
		this.order = data.order;
		this.name = data.name;
		this.vat_percentage = data.vat_percentage;
		this.image_url = data.image_url;
		this.icon = data.icon;
		this.icon_color = data.icon_color;
		this.default_currency_id = data.default_currency_id;
	}
}

export class Product extends Discountable {
	public id: number;
	public product_category_id: number;
	public order: number;
	public name: string;
	public description: string;
	public image_url: string;
	public price: string;
	public prices: Price[];
	public currency: Currency;
	public quantity: number;
	public vat_percentage: string;
	public is_warranty_product: boolean;
	public stock_type: string;
	public stock_relation: StockRelation;
	public default_stock: number;
	public linked_warranty_product_price?: Product;
	public disable_sale_catalogue_payments: boolean;
	public dont_charge_orders: boolean;

	constructor(data: CategoryProduct, categoryDefaultCurrencyId?: string | undefined | null, saleCatalogueDefaultCurrency?: Currency) {
		super(data);

		this.id = data.id;
		this.product_category_id = data.product_category_id;
		this.order = data.order;
		this.name = data.name ?? data.product.name;
		this.description = data.description;
		this.image_url = data.image_url;
		this.prices = data.prices;

		// If categoryDefaultCurrencyId is provided, we use it
		if (categoryDefaultCurrencyId != undefined && categoryDefaultCurrencyId != null && categoryDefaultCurrencyId != 'null') {
			const price = data.prices.find((price) => price.currency_id === +categoryDefaultCurrencyId);
			if (price) {
				this.price = price.amount;
				this.currency = price.currency;
			} else {
				// Find the price with the default currency
				const price = data.prices.find((price) => price.currency.name === fiatCurrency.value.nameId);
				// Set price if found, otherwise use first item price
				this.price = price?.amount || data.prices[0].amount;
				this.currency = price?.currency || data.prices[0].currency;
			}
		} else if (saleCatalogueDefaultCurrency != undefined && saleCatalogueDefaultCurrency != null) {
			// If saleCatalogueDefaultCurrency is provided, we use it
			const price = data.prices.find((price) => price.currency_id === saleCatalogueDefaultCurrency.id);
			if (price) {
				this.price = price.amount;
				this.currency = price.currency;
			} else {
				// Find the price with the default currency
				const price = data.prices.find((price) => price.currency.name === fiatCurrency.value.nameId);
				// Set price if found, otherwise use first item price
				this.price = price?.amount || data.prices[0].amount;
				this.currency = price?.currency || data.prices[0].currency;
			}
		} else {
			// If there is no default currency id, we use the default product price
			const price = data.prices.find((price) => price.currency.name === fiatCurrency.value.nameId);
			// Set price if found, otherwise use first item price
			this.price = price?.amount || data.prices[0].amount;
			this.currency = price?.currency || data.prices[0].currency;
		}

		this.quantity = 0;
		this.vat_percentage = data.product.vat_percentage;
		this.is_warranty_product = data.product.is_warranty_product;
		this.stock_type = data.product.stock_type;
		this.default_stock = data.product.default_stock;
		this.stock_relation = data.product.stock_relation;
		this.disable_sale_catalogue_payments = !!data.disable_sale_catalogue_payments;
		this.dont_charge_orders = !!data.dont_charge_orders;
	}

	public getFullPrice(): number {
		return Number(this.price);
	}
}

export class Products {
	current_page: number;
	data: Product[];
	first_page_url: string;
	from: number;
	last_page: number;
	last_page_url: string;
	links: Pagination[];
	next_page_url: string;
	path: string;
	per_page: number;
	prev_page_url: string;
	to: number;
	total: number;
	constructor() {
		this.current_page = 0;
		this.data = [];
		this.first_page_url = '';
		this.from = 0;
		this.last_page = 0;
		this.last_page_url = '';
		this.links = [];
		this.next_page_url = '';
		this.path = '';
		this.per_page = 0;
		this.prev_page_url = '';
		this.to = 0;
		this.total = 0;
	}
}

export interface Category {
	id: number;
	sale_catalogue_id: number;
	default_currency_id: null;
	order: number;
	name: string;
	vat_percentage: string;
	image: string;
	icon: null;
	icon_color: string;
	is_warranty_category: boolean;
	disable_default_printing: number;
	created_at: Date;
	updated_at: Date;
	deleted_at: null;
	discounts: any[];
	sale_catalogue: SaleCatalogue;
	category_products: CategoryProduct[];
	dont_charge_orders?: boolean;
}

export interface CategoryProduct {
	id: number;
	product_category_id: number;
	order: number;
	name: string;
	description: string;
	image_url: string;
	background_color: string;
	deleted_at: null;
	created_at: Date;
	updated_at: Date;
	prices: Price[];
	product: CategoryProductProduct;
	disable_sale_catalogue_payments?: boolean;
	dont_charge_orders?: boolean;
}

export class Price {
	id: number;
	uuid: string;
	currency_id: number;
	nickname: string;
	product_id: number;
	warranty_product_price_id: number;
	amount: string;
	tax_percentage: string;
	deleted_at: Date;
	created_at: Date;
	updated_at: Date;
	pivot?: Pivot;
	currency: Currency;
	warranty_product_price?: WarrantyProductPrice;

	constructor() {
		this.id = 0;
		this.uuid = '';
		this.currency_id = 0;
		this.nickname = '';
		this.product_id = 0;
		this.warranty_product_price_id = 0;
		this.amount = '';
		this.tax_percentage = '';
		this.deleted_at = new Date();
		this.created_at = new Date();
		this.updated_at = new Date();
		this.pivot = new Pivot();
		this.currency = new Currency();
		this.warranty_product_price = new WarrantyProductPrice();
	}
}

export class Pivot {
	product_category_product_id: number;
	product_price_id: number;

	constructor() {
		this.product_category_product_id = 0;
		this.product_price_id = 0;
	}
}

export class WarrantyProductPrice {
	id: number;
	uuid: string;
	currency_id: number;
	nickname: string;
	product_id: number;
	warranty_product_price_id: null;
	amount: string;
	tax_percentage: string;
	deleted_at: Date;
	created_at: Date;
	updated_at: Date;
	currency: Currency;

	constructor() {
		this.id = 0;
		this.uuid = '';
		this.currency_id = 0;
		this.nickname = '';
		this.product_id = 0;
		this.warranty_product_price_id = null;
		this.amount = '';
		this.tax_percentage = '';
		this.deleted_at = new Date();
		this.created_at = new Date();
		this.updated_at = new Date();
		this.currency = new Currency();
	}
}

export class CategoryProductProduct {
	id: number;
	default_product_price_id: number;
	product_category_id: number;
	order: number;
	name: string;
	description: string;
	metadata: null;
	image: null;
	purchase_price: string;
	sales_price: string;
	vat_percentage: string;
	default_stock: number;
	stock_type: string;
	stock_min: null;
	stock_max: null;
	is_warranty_product: boolean;
	warranty_product_price_id: null;
	warranty_product_id: null;
	background_color: string;
	disable_default_printing: number;
	created_at: Date;
	updated_at: Date;
	deleted_at: null;
	laravel_through_key: number;
	image_url: string;
	resolved_stock: number;
	stock_relation: StockRelation;
	discounts: null;
	default_product_price: Price;
	prices: Price[];
	constructor(data: any) {
		this.id = data.id;
		this.default_product_price_id = data.default_product_price_id;
		this.product_category_id = data.product_category_id;
		this.order = data.order;
		this.name = data.name;
		this.description = data.description;
		this.metadata = data.metadata;
		this.image = data.image;
		this.purchase_price = data.purchase_price;
		this.sales_price = data.sales_price;
		this.vat_percentage = data.vat_percentage;
		this.default_stock = data.default_stock;
		this.stock_type = data.stock_type;
		this.stock_min = data.stock_min;
		this.stock_max = data.stock_max;
		this.is_warranty_product = data.is_warranty_product;
		this.warranty_product_price_id = data.warranty_product_price_id;
		this.warranty_product_id = data.warranty_product_id;
		this.background_color = data.background_color;
		this.disable_default_printing = data.disable_default_printing;
		this.created_at = data.created_at;
		this.updated_at = data.updated_at;
		this.deleted_at = data.deleted_at;
		this.laravel_through_key = data.laravel_through_key;
		this.image_url = data.image_url;
		this.resolved_stock = data.resolved_stock;
		this.stock_relation = data.stock_relation;
		this.discounts = data.discounts;
		this.default_product_price = data.default_product_price;
		this.prices = data.prices;
	}
}

export interface SaleCatalogue {
	id: number;
	default_currency_id: number;
	default_sales_catalogue_id: null;
	name: string;
	disable_payments: number;
	disable_default_printing: number;
	default: boolean;
	created_at: Date;
	updated_at: Date;
	deleted_at: null;
	discounts: any[];
	default_currency: Currency;
}

export interface StockRelation {
	id: number;
	stockable_type: string;
	stockable_id: number;
	quantity: number;
	created_at: Date;
	updated_at: Date;
}
